import React from 'react';
import { Box, Typography, Paper, Skeleton, Grid } from '@mui/material';
import { MissionBuilderProvider, useMissionBuilder } from './MissionBuilderContext';
import { useMissionDashboard } from './hooks/useMissions';
import { MissionList } from './components/MissionList';
import { FlowBuilder } from './components/FlowBuilder';
import { InstanceList } from './components/InstanceList';

function StatCard({ label, value, loading }: { label: string; value: number | string; loading: boolean }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center' }}>
      {loading ? (
        <Skeleton variant="text" width={60} height={40} sx={{ mx: 'auto' }} />
      ) : (
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {value}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </Typography>
    </Paper>
  );
}

function MissionBuilderContent() {
  const { view } = useMissionBuilder();
  const { stats, loading: statsLoading } = useMissionDashboard();

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Mission Builder
      </Typography>

      {/* Dashboard Stats (only on list view) */}
      {view === 'list' && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatCard label="Active Templates" value={stats?.activeTemplates ?? 0} loading={statsLoading} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatCard label="Instances Today" value={stats?.instancesToday ?? 0} loading={statsLoading} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatCard label="Completed Today" value={stats?.completedToday ?? 0} loading={statsLoading} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatCard label="Completion Rate" value={`${stats?.completionRate ?? 0}%`} loading={statsLoading} />
          </Grid>
        </Grid>
      )}

      {/* Main Content */}
      <Box sx={{ minHeight: view === 'builder' ? 'calc(100vh - 280px)' : 'auto' }}>
        {view === 'list' && <MissionList />}
        {view === 'builder' && <FlowBuilder />}
        {view === 'instances' && <InstanceList />}
      </Box>
    </Box>
  );
}

export default function MissionBuilder() {
  return (
    <MissionBuilderProvider>
      <MissionBuilderContent />
    </MissionBuilderProvider>
  );
}
